import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface RequestPart {
  readonly requestCode: string;
  readonly requestType: string;
  readonly status: string;
}
interface ResponsePart {
  readonly responseCode: string;
  readonly outputText: string;
  readonly confidence: string;
  readonly confidenceScore: string;
  readonly validationStatus: string;
  readonly hallucinationRisk: string;
  readonly isAdvisory: boolean;
  readonly requiresApproval: boolean;
  readonly approvalStatus: string;
}
interface KnowledgeResult {
  readonly request: RequestPart;
  readonly response: ResponsePart | null;
}

type Mode = 'query' | 'search' | 'summary';
const SOURCE_TYPES = ['DOCUMENT', 'ARTICLE', 'REPORT', 'POLICY', 'RECORD'];

/**
 * M38 — Knowledge Intelligence Portal (§10/§11/§15). Natural-language query,
 * unified AI search and summary generation over enterprise knowledge (referenced
 * from M35 / M23 / M36 / M37). Every answer flows through the governed pipeline —
 * §18 data-protection gates, §21 hallucination checks and §20 audit — and is
 * always advisory (BR-01). The portal surfaces the response together with its
 * confidence band, validation status, hallucination risk and whether human
 * approval is required (BR-02). Querying + summarizing require KNOWLEDGE_QUERY;
 * unified search requires KNOWLEDGE_SEARCH. A 403 surfaces a clear permission
 * message.
 */
export function KnowledgeIntelligencePortal(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('query');
  const [question, setQuestion] = useState('');
  const [scope, setScope] = useState('');
  const [sourceType, setSourceType] = useState(SOURCE_TYPES[0]);
  const [sourceRef, setSourceRef] = useState('');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<KnowledgeResult | null>(null);

  const reset = (m: Mode): void => {
    setMode(m);
    setError(null);
    setResult(null);
  };

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setResult(null);
    const permission = mode === 'search' ? 'KNOWLEDGE_SEARCH' : 'KNOWLEDGE_QUERY';
    if (mode === 'summary') {
      if (!sourceRef.trim() || !content.trim()) { setError(t('m38.knowledge.summaryRequired')); return; }
    } else if (!question.trim()) {
      setError(mode === 'search' ? t('m38.knowledge.searchRequired') : t('m38.knowledge.questionRequired'));
      return;
    }
    setBusy(true);
    try {
      let body: Record<string, unknown>;
      if (mode === 'query') body = { question: question.trim(), scope: scope.trim() || undefined };
      else if (mode === 'search') body = { query: question.trim(), scope: scope.trim() || undefined };
      else body = { sourceType, sourceRef: sourceRef.trim(), content: content.trim() };
      const r = await apiRequest<{ data: KnowledgeResult }>(`/v1/ai/knowledge/${mode}`, { method: 'POST', body, token });
      setResult(r.data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(t('m38.knowledge.notAuthorised', { permission }));
      } else {
        setError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="knowledge-intelligence-portal">
      <h1>{t('m38.knowledge.title')}</h1>

      <nav aria-label="knowledge-modes">
        <Button variant={mode === 'query' ? 'primary' : 'secondary'} type="button" aria-label="mode-query" aria-pressed={mode === 'query'} onClick={() => reset('query')}>{t('m38.knowledge.modeQuery')}</Button>
        <Button variant={mode === 'search' ? 'primary' : 'secondary'} type="button" aria-label="mode-search" aria-pressed={mode === 'search'} onClick={() => reset('search')}>{t('m38.knowledge.modeSearch')}</Button>
        <Button variant={mode === 'summary' ? 'primary' : 'secondary'} type="button" aria-label="mode-summary" aria-pressed={mode === 'summary'} onClick={() => reset('summary')}>{t('m38.knowledge.modeSummary')}</Button>
      </nav>

      <form aria-label="knowledge-form" onSubmit={(e) => void submit(e)}>
        {mode === 'summary' ? (
          <>
            <label>{t('m38.knowledge.sourceType')}
              <select aria-label="field-source-type" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
                {SOURCE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>{t('m38.knowledge.sourceRef')}<input aria-label="field-source-ref" value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} placeholder={t('m38.knowledge.sourceRefPh')} /></label>
            <label>{t('m38.knowledge.content')}
              <textarea aria-label="field-content" value={content} onChange={(e) => setContent(e.target.value)} rows={5} placeholder={t('m38.knowledge.contentPh')} />
            </label>
          </>
        ) : (
          <>
            <label>{mode === 'search' ? t('m38.knowledge.searchQuery') : t('m38.knowledge.question')}
              <input aria-label="field-question" value={question} onChange={(e) => setQuestion(e.target.value)} />
            </label>
            <label>{t('m38.knowledge.scope')}<input aria-label="field-scope" value={scope} onChange={(e) => setScope(e.target.value)} placeholder={t('m38.knowledge.scopePh')} /></label>
          </>
        )}
        <Button variant="primary" type="submit" aria-label="submit-knowledge" disabled={busy}>{busy ? t('m38.knowledge.working') : t('m38.knowledge.submit')}</Button>
      </form>

      {error && <p role="alert" aria-label="knowledge-error">{error}</p>}

      {result && (
        <section aria-label="knowledge-result">
          <h2>{t('m38.knowledge.responseHeading')}</h2>
          <p aria-label="result-request">{t('m38.knowledge.request', { code: result.request.requestCode, type: result.request.requestType, status: result.request.status })}</p>
          {result.response ? (
            <>
              <blockquote aria-label="result-output">{result.response.outputText}</blockquote>
              <dl>
                <dt>{t('m38.knowledge.confidence')}</dt>
                <dd aria-label="result-confidence">{decimal(result.response.confidence)} ({decimal(result.response.confidenceScore)})</dd>
                <dt>{t('m38.knowledge.validation')}</dt>
                <dd aria-label="result-validation">{result.response.validationStatus}</dd>
                <dt>{t('m38.knowledge.hallucinationRisk')}</dt>
                <dd aria-label="result-hallucination">{result.response.hallucinationRisk}</dd>
                <dt>{t('m38.knowledge.advisoryOnly')}</dt>
                <dd aria-label="result-advisory">{result.response.isAdvisory ? t('common.yes') : t('common.no')}</dd>
                <dt>{t('m38.knowledge.humanApproval')}</dt>
                <dd aria-label="result-approval">{result.response.requiresApproval ? t('m38.knowledge.approvalRequired', { status: result.response.approvalStatus }) : t('m38.knowledge.notRequired')}</dd>
              </dl>
            </>
          ) : (
            <p aria-label="result-blocked">{t('m38.knowledge.noResponse')}</p>
          )}
        </section>
      )}
    </main>
  );
}
